import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Container,
  Paper,
  Avatar,
  Stack,
  Divider,
  useTheme,
  Link as MuiLink
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { auth } from "../utils/firebase";
import { useAuth } from "../context/AuthContext";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import PlaceIcon from "@mui/icons-material/Place";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import FavoriteIcon from "@mui/icons-material/Favorite";
import GroupIcon from "@mui/icons-material/Group";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import EmojiPeopleIcon from "@mui/icons-material/EmojiPeople";
import StarIcon from "@mui/icons-material/Star";

export default function LandingPage() {
  const [events, setEvents] = useState([]);
  const [hostsImageFailed, setHostsImageFailed] = useState(false);
  const { user } = useAuth();
  const [userEventIds, setUserEventIds] = useState(new Set());
  const [userEventStatus, setUserEventStatus] = useState({}); // eventId -> status
  const navigate = useNavigate();
  const theme = useTheme();

  useEffect(() => {
    async function fetchEvents() {
      try {
        const base = (process.env.REACT_APP_BACKEND_URL || "");
        const resp = await fetch(`${base}/api/event/list-public`);
        const json = await resp.json().catch(() => ({}));
        if (!resp.ok) throw new Error(json?.message || `HTTP ${resp.status}`);
        const arr = Array.isArray(json.events) ? json.events : [];
        setEvents(arr.slice(0, 6));
      } catch {
        setEvents([]);
      }
    }
    fetchEvents();
  }, []);

  // Fetch participant docs for current user to filter out signed-up events
  useEffect(() => {
    if (!user) { setUserEventIds(new Set()); setUserEventStatus({}); return; }
    (async () => {
      try {
        // Keep using Firestore for user participant lookups (rules should restrict this to own docs)
        const { collection, getDocs, query, where } = await import('firebase/firestore');
        const { db } = await import('../utils/firebase');
        const partsQ = query(collection(db, 'participants'), where('userId', '==', user.uid));
        const snap = await getDocs(partsQ);
        const ids = new Set();
        const statusMap = {};
        snap.docs.forEach(d => {
          const data = d.data();
          if (data.status !== 'cancelled') ids.add(data.eventId);
          statusMap[data.eventId] = data.status;
        });
        setUserEventIds(ids);
        setUserEventStatus(statusMap);
      } catch {
        setUserEventIds(new Set());
        setUserEventStatus({});
      }
    })();
  }, [user]);

  // testimonials
  const testimonials = [
    {
      quote:
        "Everyone was very nice and it was well organized and felt safe.",
        rating: 5,
    },
    {
      quote:
        "Very organized, not too loud, coffee bar was top notch, workers were incredible and gave excellent service.",
        rating: 5,
    },
    {
      quote:
        "Well organized event. Advertising for the event on social media was great. The atmosphere for the event was fantastic.",
      rating: 5,
    },
  ];

  // Purpose/mission statements
  const purposeStats = [
    {
      icon: <FavoriteIcon color="error" sx={{ fontSize: 40 }} />,
      label: "Faith-Focused",
      desc: "Meet other Christians who share your values and passions.",
    },
    {
      icon: <GroupIcon color="primary" sx={{ fontSize: 40 }} />,
      label: "Community",
      desc: "Connect in a safe, welcoming, and supportive environment.",
    },
    {
      icon: <LightbulbIcon color="warning" sx={{ fontSize: 40 }} />,
      label: "Meaningful Matches",
      desc: "Find lasting relationships rooted in Christ.",
    },
    {
      icon: <EmojiPeopleIcon sx={{ color: theme.palette.success.main, fontSize: 40 }} />,
      label: "Fun & Friendly",
      desc: "Enjoy engaging fellowship and meaningful conversations.",
    },
  ];

  return (
    <Box
      sx={{
        minHeight: "calc(100vh - 64px)",
        background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
        pb: { xs: 2, sm: 4, md: 6 },
      }}
    >
      {/* Hero */}
      <Box
        sx={{
          py: { xs: 4, sm: 8, md: 12 },
          background: "linear-gradient(120deg, #a8edea 0%, #fed6e3 100%)",
          mb: { xs: 2, sm: 4, md: 6 },
        }}
      >
        <Container maxWidth="md" sx={{ px: { xs: 1, sm: 2 } }}>
          <Grid container spacing={{ xs: 2, sm: 4 }} alignItems="center" direction={{ xs: 'column-reverse', md: 'row' }}>
            <Grid item xs={12} md={6}>
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 800,
                  mb: 3,
                  color: "#222",
                  textShadow: "1px 2px 4px #fff6",
                  fontSize: { xs: 28, sm: 36, md: 48 },
                  textAlign: { xs: 'center', md: 'left' }
                }}
              >
                Find your Christian Match
              </Typography>
              <Typography variant="h5" sx={{ mb: 4, color: "#444", fontSize: { xs: 16, sm: 20 }, textAlign: { xs: 'center', md: 'left' } }}>
                In-person speed dating events for Bible-believing Christians. Events located out of Akron, OH. Established 2024.
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: { xs: 'center', md: 'flex-start' } }}>
                <Button
                  variant="contained"
                  size="large"
                  color="primary"
                  sx={{ boxShadow: 3, fontWeight: "bold", px: 4, width: { xs: '100%', sm: 'auto' } }}
                  onClick={() => navigate("/register")}
                >
                  Get Started
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: "flex", justifyContent: "center", width: '100%' }}>
                <img
                  src="/hero-illustration.svg"
                  alt="Christian Speed Dating"
                  style={{ width: '100%', maxWidth: 340, borderRadius: 16, boxShadow: "0 6px 32px #0002" }}
                  onError={e => (e.target.style.display = "none")}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Upcoming Events */}
  <Container maxWidth="lg" sx={{ mb: { xs: 3, sm: 6, md: 8 }, px: { xs: 1, sm: 2 } }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, textAlign: "center" }}>
          Upcoming Events
        </Typography>
  <Grid container spacing={{ xs: 2, sm: 4 }} justifyContent="center">
          {events.length === 0 && (
            <Grid item xs={12}>
              <Paper sx={{ p: 4, textAlign: "center", bgcolor: '#fff' }}>
                <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                  No upcoming events yet. Check back soon!
                </Typography>
              </Paper>
            </Grid>
          )}
          {events.map((ev) => (
            <Grid item xs={12} sm={6} md={4} key={ev.id}>
              <Card
                sx={{
                  borderRadius: 4,
                  boxShadow: 4,
                  transition: "transform .2s",
                  "&:hover": { transform: "scale(1.03)" },
                  cursor: "pointer",
                  width: '100%',
                  minHeight: 320,
                  display: 'flex',
                  flexDirection: 'column',
                }}
                onClick={() => navigate(user ? `/event/${ev.id}` : "/login")}
              >
                {ev.imageUrl ? (
                  <CardMedia
                    component="img"
                    sx={{ height: { xs: 120, sm: 160 }, objectFit: 'cover' }}
                    image={ev.imageUrl}
                    alt={ev.name}
                  />
                ) : (
                  <Box
                    sx={{
                      height: { xs: 120, sm: 160 },
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: "#ece9f6",
                    }}
                  >
                    <CalendarMonthIcon fontSize="large" color="primary" />
                  </Box>
                )}
                <CardContent sx={{ flex: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, fontSize: { xs: 16, sm: 18 } }}>
                    {ev.name}
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <CalendarMonthIcon fontSize="small" color="action" />
                    <Typography variant="body2">
                      {(() => {
                        if (!ev?.date) return '';
                        let d;
                        if (ev.date?.seconds) d = new Date(ev.date.seconds * 1000);
                        else d = new Date(ev.date);
                        return isNaN(d) ? '' : d.toLocaleDateString();
                      })()}
                    </Typography>
                  </Stack>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    {/* Pin icon (same style as admin pin) */}
                    <svg xmlns="http://www.w3.org/2000/svg" height="20" viewBox="0 0 24 24" width="20" fill="#4285F4"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>
                    {(() => {
                      const isApprovedPaid = user && userEventStatus[ev.id] === 'approved_paid';
                      const loc = isApprovedPaid ? (ev.specificLocation || ev.generalLocation) : (ev.generalLocation || 'TBA');
                      return (
                        <Typography variant="body2">{loc}</Typography>
                      );
                    })()}
                    {/* No map link on landing; only show pin and location text */}
                  </Stack>
                  <Typography variant="body2" sx={{ minHeight: 44, color: "#555", mb: 2, fontSize: { xs: 13, sm: 15 } }}>
                    {ev.description ? ev.description.slice(0, 80) + (ev.description.length > 80 ? "..." : "") : "A Christian singles event."}
                  </Typography>
          {(ev.mode === 'signups' || (ev.mode === 'limited-signups' && ev.signupLimit && ev.signupLimit !== 'none')) ? (
                    <Button
                      variant="outlined"
                      color="primary"
                      endIcon={<ArrowForwardIcon />}
                      fullWidth
            onClick={() => navigate(user ? `/event/${ev.id}` : "/login")}
                      sx={{ fontSize: { xs: 13, sm: 15 } }}
                    >
            {user && userEventIds.has(ev.id) ? 'View Event' : 'View & Sign Up'}
                    </Button>
                  ) : (
                    <Button
                      variant="outlined"
                      color="primary"
                      fullWidth
                      disabled
                    >
                      Sign-ups Closed
                    </Button>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Purpose & Features */}
      <Container maxWidth="md" sx={{ mb: 10 }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: 5,
            background: "rgba(255,255,255,0.95)",
            mb: 4
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, textAlign: "center" }}>
            Our Purpose
          </Typography>
          <Typography sx={{ mb: 3, textAlign: "center", color: "#555" }}>
            We believe that meaningful relationships are best built on faith, trust, and genuine connection. Our events are designed to bring Christian singles together in a supportive and fun environment.
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "center", width: '100%' }}>
                {hostsImageFailed ? (
                  <Paper
                    elevation={0}
                    sx={{
                      width: '100%',
                      maxWidth: 340,
                      p: 2,
                      borderRadius: 2,
                      textAlign: 'center',
                      bgcolor: '#f8fafc',
                      color: 'text.secondary'
                    }}
                  >
                  </Paper>
                ) : (
                  <img
                    src="/hosts.jpeg"
                    alt="Kelsey and Sierra, the hosts of Christian Speed Dating"
                    style={{ width: '100%', maxWidth: 340, borderRadius: 16, boxShadow: "0 6px 32px #0002" }}
                    onError={() => setHostsImageFailed(true)}
                  />
                )}
          </Box>
          <Typography sx={{ mt: 3, mb: 3, textAlign: "center", color: "#555" }}>
            Hi! We are your hosts! From left to right: Kelsey and Sierra. We are sisters from northeast Ohio. Kelsey is the registration host. She is in charge of all social media, answering questions, registrations, payment collection, form prep for the event, confirmation texts, reminder texts, etc. Sierra is the day-of host. She is in charge of all volunteers and being sure the event runs smoothly.
          </Typography>
          <Typography sx={{ fontWeight: 700, mb: 3, textAlign: "center" }}>
            Lovely to meet you all! Reach out with any questions. 
          </Typography> 
          <Grid container spacing={4}>
            {purposeStats.map((item, i) => (
              <Grid item xs={12} sm={6} md={3} key={i}>
                <Box
                  sx={{
                    p: 3,
                    borderRadius: 4,
                    textAlign: "center",
                    background: "#f8fafc"
                  }}
                >
                  {item.icon}
                  <Typography sx={{ fontWeight: 700, mt: 2 }}>{item.label}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {item.desc}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Paper>
      </Container>

      {/* Testimonials */}
      <Container maxWidth="md">
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, textAlign: "center" }}>
          What People Are Saying
        </Typography>
        <Grid container spacing={4} justifyContent="center">
          {testimonials.map((t, i) => (
            <Grid item xs={12} md={4} key={i}>
              <Paper
                elevation={3}
                sx={{
                  p: 4,
                  borderRadius: 4,
                  minHeight: 240,
                  background: "#fff",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center"
                }}
              >
                {/* <Avatar
                  src={t.avatar}
                  alt={t.name}
                  sx={{ width: 64, height: 64, mb: 2, boxShadow: 2 }}
                /> */}
                <Typography variant="body1" sx={{ fontStyle: "italic", mb: 2, color: "#444" }}>
                  "{t.quote}"
                </Typography>
                <Divider sx={{ width: 40, mb: 1 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{t.name}</Typography>
                <Box sx={{ mt: 1 }}>
                  {/* {[...Array(t.rating)].map((_, idx) => (
                    <StarIcon key={idx} fontSize="small" sx={{ color: "#FFD700" }} />
                  ))} */}
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Call to Action */}
      <Container maxWidth="sm" sx={{ mt: 10 }}>
        <Paper
          elevation={4}
          sx={{
            p: 5,
            borderRadius: 6,
            textAlign: "center",
            background: "linear-gradient(90deg, #c9ffbf 0%, #ffafbd 100%)",
            boxShadow: "0 8px 24px #0002"
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
            Ready to meet someone special?
          </Typography>
          <Typography sx={{ mb: 3 }}>
            Sign up now and join our next Christian Speed Dating event. Your match might be just a conversation away!
          </Typography>
          <Button
            variant="contained"
            size="large"
            color="primary"
            sx={{ fontWeight: "bold", px: 5, boxShadow: 2 }}
            onClick={() => navigate("/register")}
          >
            Get Started
          </Button>
        </Paper>
      </Container>

      {/* Footer */}
      <Box sx={{ mt: 10, textAlign: "center", color: "#888", py: 3 }}>
        <Typography variant="body2">
          &copy; {new Date().getFullYear()} Christian Speed Dating &mdash; 
          <MuiLink href="mailto:christianspeeddatingohio@gmail.com" sx={{ ml: 1 }}>
            Contact Us
          </MuiLink>
        </Typography>
      </Box>
    </Box>
  );
}